import React from 'react';
import { View } from 'react-native';
import { Text } from '@/components/ui/text';

interface TableGroup {
  caption?: string | null;
  headers: string[];
  rows: string[][];
}

interface TablePayload {
  // Current shape: one or more captioned tables.
  groups?: TableGroup[];
  // Back-compat: a single ungrouped table.
  headers?: string[];
  rows?: string[][];
}

export interface GenericTableCardProps {
  payload: Record<string, unknown>;
}

/** Normalise either payload shape into a list of non-empty table groups. */
const toGroups = (payload: Record<string, unknown>): TableGroup[] => {
  const p = payload as TablePayload;
  const groups = Array.isArray(p.groups)
    ? p.groups
    : Array.isArray(p.headers)
      ? [{ headers: p.headers, rows: p.rows ?? [] }]
      : [];
  return groups.filter(
    (g) => (g.headers?.length ?? 0) > 0 && (g.rows?.length ?? 0) > 0,
  );
};

export const GenericTableCard: React.FC<GenericTableCardProps> = ({
  payload,
}) => {
  const groups = toGroups(payload);

  if (groups.length === 0) {
    return null;
  }

  return (
    <View className="w-full py-2 gap-3">
      {groups.map((group, groupIndex) => (
        <View key={groupIndex} className="w-full py-2 px-4 bg-muted rounded-lg">
          {group.caption ? (
            <Text className="font-semibold text-sm pb-2">{group.caption}</Text>
          ) : null}

          {/* Header row */}
          <View className="flex-row pb-2 border-b border-border">
            {group.headers.map((header, index) => (
              <View key={index} style={{ flex: 1 }} className="px-2">
                <Text className="font-semibold text-sm">{header}</Text>
              </View>
            ))}
          </View>

          {/* Data rows */}
          {group.rows.map((row, rowIndex) => (
            <View
              key={rowIndex}
              className="flex-row py-2 border-b border-border"
            >
              {row.map((cell, cellIndex) => (
                <View key={cellIndex} style={{ flex: 1 }} className="px-2">
                  <Text className="text-sm">{cell}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
};
